import React, { useState, useEffect, useCallback } from 'react';
import { 
  MessageSquare, 
  User, 
  Calendar,
  ArrowLeft,
  Search,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { adminAPI } from '../services/api';
import { UserConversationsResponse, Conversation } from '../types';

interface UserConversationsProps {
  userId: number;
  onBack: () => void;
}

const UserConversations: React.FC<UserConversationsProps> = ({ userId, onBack }) => {
  const [data, setData] = useState<UserConversationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [expandedConversations, setExpandedConversations] = useState<Set<number>>(new Set());

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUserConversations(userId, page, 10);
      setData(response);
    } catch (error) {
      console.error('Error loading user conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, page]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const toggleConversation = (conversationId: number) => {
    const newExpanded = new Set(expandedConversations);
    if (newExpanded.has(conversationId)) {
      newExpanded.delete(conversationId);
    } else {
      newExpanded.add(conversationId);
    }
    setExpandedConversations(newExpanded);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMessageDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Erreur lors du chargement des conversations</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </button>
            <div>
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Conversations de {data.user.firstName} {data.user.lastName}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {data.user.email} • {data.pagination.totalConversations} conversation(s)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {data.conversations.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune conversation</h3>
            <p className="mt-1 text-sm text-gray-500">
              Cet utilisateur n'a pas encore de conversations.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {data.conversations.map((conversation) => {
              const isExpanded = expandedConversations.has(conversation.id);
              const userMessages = conversation.messages.filter(msg => msg.isUserMessage);
              const assistantMessages = conversation.messages.filter(msg => !msg.isUserMessage);
              
              return (
                <li key={conversation.id} className="px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => toggleConversation(conversation.id)}
                          className="flex items-center space-x-2 text-left hover:text-primary-600"
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {conversation.title}
                          </h3>
                        </button>
                      </div>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Créée le {formatDate(conversation.created_at)}
                        </span>
                        <span className="flex items-center">
                          <MessageSquare className="h-4 w-4 mr-1" />
                          {conversation.messages.length} message(s)
                        </span>
                        <span className="text-green-600">
                          {userMessages.length} question(s)
                        </span>
                        <span className="text-blue-600">
                          {assistantMessages.length} réponse(s)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Messages */}
                  {isExpanded && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <div className="space-y-4">
                        {conversation.messages.map((message, index) => (
                          <div
                            key={message.id}
                            className={`flex ${message.isUserMessage ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-3xl px-4 py-2 rounded-lg ${
                                message.isUserMessage
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-gray-100 text-gray-900'
                              }`}
                            >
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="text-xs font-medium opacity-75">
                                  {message.isUserMessage ? 'Utilisateur' : 'Assistant'}
                                </span>
                                <span className="text-xs opacity-75">
                                  {formatMessageDate(message.createdAt)}
                                </span>
                              </div>
                              <div className="whitespace-pre-wrap text-sm">
                                {message.content}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Pagination */}
        {data.pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(Math.min(data.pagination.totalPages, page + 1))}
                disabled={page === data.pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{page}</span> sur{' '}
                  <span className="font-medium">{data.pagination.totalPages}</span> ({data.pagination.totalConversations} conversation(s) au total)
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setPage(Math.min(data.pagination.totalPages, page + 1))}
                    disabled={page === data.pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserConversations;
